import { useGetQuery, type UseGetQueryOptions } from "@src/appV2/api";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { type ReviewsViewContext } from "@src/appV2/redesign/ShiftDiscovery/useWorkplaceModalsDataContext";
import { type UseQueryResult } from "@tanstack/react-query";
import { z } from "zod";

import { getCommentUrl } from "./getCommentUrl";
import { workplaceCommentSchema } from "./useWorkplaceComments";

const getCommentRequestSchema = z
  .object({
    viewContext: z.enum(["shifts", "placements"]).optional(),
  })
  .optional();

interface GetCommentParams {
  workplaceId: string;
  commentId: string;
  viewContext?: ReviewsViewContext;
}

const getCommentResponseSchema = z.object({
  data: workplaceCommentSchema,
});

export type GetCommentResponse = z.infer<typeof getCommentResponseSchema>;

export function useGetComment(
  params: GetCommentParams,
  options: UseGetQueryOptions<GetCommentResponse> = {}
): UseQueryResult<GetCommentResponse> {
  const { commentId, workplaceId, viewContext } = params;
  return useGetQuery({
    url: getCommentUrl({ commentId, workplaceId }),
    queryParams: viewContext ? { viewContext } : undefined,
    requestSchema: getCommentRequestSchema,
    responseSchema: getCommentResponseSchema,
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_REVIEW_COMMENT_FAILURE,
      userErrorMessage: "Something went wrong while fetching comment",
    },
    ...options,
  });
}
